import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Plus,
    Settings,
    Search,
    Filter,
    Package,
    BarChart3,
    Truck,
    Wrench,
    Monitor,
    Smartphone,
    TrendingUp,
    AlertCircle,
    DollarSign,
    Box,
    Eye,
    Edit3,
    MoreVertical,
    ChevronDown,
    User,
    Building2,
    Calendar,
    Hammer,
    LayoutGrid,
    Anchor,
    Printer,
    Armchair,
    ArrowUpDown,
    Trash2,
    AlertTriangle,
    RotateCcw,
    Clock,
    ArrowLeft,
    ChevronRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { cn } from '../../components/ui/utils';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuLabel,
    DropdownMenuSeparator,
} from "../../components/ui/dropdown-menu";
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
    PaginationEllipsis,
} from "../../components/ui/pagination";
import AssetSettingsPage from './AssetSettings';
import ElectricalRegisterList from './components/ElectricalRegisterList';
import LiftingRegisterList from './components/LiftingRegisterList';
import MaintenanceCalendar from './components/MaintenanceCalendar';
import api from '../../utils/api';
import { Asset, AssetType, AssetStatus } from '../../types/asset';
import AssetReports from './reports/AssetReports';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { toast } from 'sonner';
import { toSydneyDate } from '../../utils/helpers';
import AccessRestricted from '../../components/AccessRestricted';

const AssetDashboard: React.FC = () => {
    const ability = useAbility(AbilityContext);

    if (!ability.can(ACTIONS.READ, RESOURCES.ASSET)) {
        return <AccessRestricted />;
    }
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const initialTab = searchParams.get('tab') || 'assets';

    const [activeTab, setActiveTab] = useState(initialTab);

    // Fallback to 'assets' tab if user doesn't have permission for the current tab
    useEffect(() => {
        const tabPermissions: Record<string, boolean> = {
            assets: true,
            electrical: ability.can(ACTIONS.READ, RESOURCES.ASSET_ELECTRICAL_REGISTER_TAB),
            lifting: ability.can(ACTIONS.READ, RESOURCES.ASSET_LIFTING_REGISTER_TAB),
            maintenance: ability.can(ACTIONS.READ, RESOURCES.ASSET_SHCEDULED_MAINTENANCE_TAB),
            reports: ability.can(ACTIONS.READ, RESOURCES.ASSET_REPORT_TAB),
        };
        if (!tabPermissions[activeTab]) {
            setActiveTab('assets');
        }
    }, [activeTab, ability]);

    const [selectedType, setSelectedType] = useState<string>('All');
    const [statusFilter, setStatusFilter] = useState<string>('All Status');
    const [sortBy, setSortBy] = useState<string>('newest');
    const [searchQuery, setSearchQuery] = useState('');
    const [allAssets, setAllAssets] = useState<Asset[]>([]);
    const [loading, setLoading] = useState(true);
    const [actionItemsCount, setActionItemsCount] = useState<number>(0);

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 25;

    // Delete dialog state
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showDeleteWarning, setShowDeleteWarning] = useState(false);
    const [deleteWarningStatus, setDeleteWarningStatus] = useState('');
    const [assetToDelete, setAssetToDelete] = useState<Asset | null>(null);
    const [selectedApprover, setSelectedApprover] = useState('');
    const [approvers, setApprovers] = useState<{ id: number; name: string; userRole: string; email: string }[]>([]);
    const [deleting, setDeleting] = useState(false);

    // Recycle bin state
    const [recycledAssets, setRecycledAssets] = useState<(Asset & { daysRemaining?: number })[]>([]);
    const [recycleLoading, setRecycleLoading] = useState(false);
    const [restoringId, setRestoringId] = useState<number | null>(null);
    const [showRecycleBin, setShowRecycleBin] = useState(false);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, selectedType, statusFilter]);

    useEffect(() => {
        const tab = searchParams.get('tab');
        if (tab && tab !== activeTab) {
            setActiveTab(tab);
        }
    }, [location.search]);

    useEffect(() => {
        const fetchAssets = async () => {
            try {
                setLoading(true);
                const query = new URLSearchParams();
                if (searchQuery) query.append('q', searchQuery);

                const response = await api.get<Asset[]>(`/api/assets?${query.toString()}`);
                setAllAssets(response);
            } catch (error) {
                console.error('Error fetching assets:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchAssets();
    }, [searchQuery]);

    // Fetch approvers for delete dialog
    const fetchApprovers = useCallback(async () => {
        try {
            const data = await api.get<{ id: number; name: string; userRole: string; email: string }[]>('/api/assets/approvers');
            setApprovers(data);
        } catch (error) {
            console.error('Error fetching approvers:', error);
        }
    }, []);

    // Fetch recycle bin contents
    const fetchRecycleBin = useCallback(async () => {
        try {
            setRecycleLoading(true);
            const data = await api.get<(Asset & { daysRemaining?: number })[]>('/api/assets/recycle-bin');
            setRecycledAssets(data);
        } catch (error) {
            console.error('Error fetching recycle bin:', error);
        } finally {
            setRecycleLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRecycleBin();
    }, [fetchRecycleBin]);

    useEffect(() => {
        if (showRecycleBin) {
            fetchRecycleBin();
        }
    }, [showRecycleBin, fetchRecycleBin]);

    useEffect(() => {
        fetchApprovers();
    }, [fetchApprovers]);

    useEffect(() => {
        const fetchActionItemsCount = async () => {
            try {
                const res = await api.get<any>('/api/assets/action-items');
                const count = (res.repairApprovals?.length || 0) +
                              (res.damagedAssets?.length || 0) +
                              (res.overdueMaintenance?.length || 0) +
                              (res.upcomingReminders?.length || 0);
                setActionItemsCount(count);
            } catch (err) {
                console.error('Failed to fetch action items count:', err);
            }
        };

        fetchActionItemsCount();
    }, []);

    // Delete handlers
    const handleDeleteClick = (asset: Asset) => {
        const deletableStatuses = ['Damaged', 'Needs Repair', 'Disposed', 'Lost'];
        if (deletableStatuses.includes(asset.status)) {
            setAssetToDelete(asset);
            setSelectedApprover('');
            setShowDeleteConfirm(true);
        } else {
            setDeleteWarningStatus(asset.status);
            setShowDeleteWarning(true);
        }
    };

    const confirmDelete = async () => {
        if (!assetToDelete || !selectedApprover) return;
        try {
            setDeleting(true);
            await api.post(`/api/assets/${assetToDelete.id}/soft-delete`, { approvedById: parseInt(selectedApprover) });
            toast.success(`${assetToDelete.name} has been moved to the recycle bin.`);
            setAllAssets(prev => prev.filter(a => a.id !== assetToDelete.id));
            fetchRecycleBin();
            setShowDeleteConfirm(false);
            setAssetToDelete(null);
            setSelectedApprover('');
        } catch (error: any) {
            console.error('Error deleting asset:', error);
            toast.error(error.response?.data?.message || 'Failed to delete asset');
        } finally {
            setDeleting(false);
        }
    };

    const handleRestore = async (asset: Asset) => {
        try {
            setRestoringId(asset.id);
            await api.post(`/api/assets/${asset.id}/restore`, {});
            toast.success(`${asset.name} has been restored.`);
            setRecycledAssets(prev => prev.filter(a => a.id !== asset.id));
            // Refresh main list if needed
            const query = new URLSearchParams();
            if (searchQuery) query.append('q', searchQuery);
            const response = await api.get<Asset[]>(`/api/assets?${query.toString()}`);
            setAllAssets(response);
        } catch (error: any) {
            console.error('Error restoring asset:', error);
            toast.error(error.response?.data?.message || 'Failed to restore asset');
        } finally {
            setRestoringId(null);
        }
    };

    const assets = useMemo(() => {
        const filtered = allAssets.filter(a => {
            const matchesType = selectedType === 'All' || a.type === selectedType;
            let matchesStatus = statusFilter === 'All Status' || a.status === statusFilter;

            if (statusFilter === 'Unassigned') {
                matchesStatus = !a.assignedToUser && !a.assignedToDepartment;
            }

            return matchesType && matchesStatus;
        });

        return [...filtered].sort((a, b) => {
            switch (sortBy) {
                case 'newest':
                    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
                case 'oldest':
                    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
                case 'name_asc':
                    return a.name.localeCompare(b.name);
                case 'name_desc':
                    return b.name.localeCompare(a.name);
                case 'value_high':
                    return (Number(b.purchasePrice) || 0) - (Number(a.purchasePrice) || 0);
                case 'value_low':
                    return (Number(a.purchasePrice) || 0) - (Number(b.purchasePrice) || 0);
                default:
                    return 0;
            }
        });
    }, [allAssets, selectedType, statusFilter, sortBy]);

    const paginatedAssets = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return assets.slice(startIndex, startIndex + itemsPerPage);
    }, [assets, currentPage]);

    const totalPages = Math.ceil(assets.length / itemsPerPage);

    const stats = useMemo(() => {
        const total = assets.length;
        const value = assets.reduce((acc, curr) => acc + (Number(curr.purchasePrice) || 0), 0);
        const active = assets.filter(a => a.status === 'Assigned').length;
        const available = assets.filter(a => a.status === 'Available').length;
        const needAttention = assets.filter(a => a.status === 'Needs Repair' || a.status === 'Damaged').length;

        return { total, value, active, available, needAttention };
    }, [assets]);

    const getTypeCount = (type: string) => {
        return allAssets.filter(a => {
            let matchesStatus = statusFilter === 'All Status' || a.status === statusFilter;
            if (statusFilter === 'Unassigned') {
                matchesStatus = !a.assignedToUser && !a.assignedToDepartment;
            }
            return matchesStatus && (type === 'All' || a.type === type);
        }).length;
    };

    const getStatusCount = (status: string) => {
        return allAssets.filter(a => {
            const matchesType = selectedType === 'All' || a.type === selectedType;
            if (!matchesType) return false;

            if (status === 'All Status') return true;
            if (status === 'Unassigned') return !a.assignedToUser && !a.assignedToDepartment;
            return a.status === status;
        }).length;
    };

    const assetTypes = [
        { id: 'All', label: 'All', icon: <Box size={16} />, count: getTypeCount('All') },
        { id: 'Vehicle', label: 'Vehicles', icon: <Truck size={16} />, count: getTypeCount('Vehicle') },
        { id: 'Plant & Equipment', label: 'Plant & Equipment', icon: <Wrench size={16} />, count: getTypeCount('Plant & Equipment') },
        { id: 'Tools', label: 'Tools', icon: <Hammer size={16} />, count: getTypeCount('Tools') },
        { id: 'IT Equipment', label: 'IT Equipment', icon: <Monitor size={16} />, count: getTypeCount('IT Equipment') },
        { id: 'Mobile Devices', label: 'Mobile Devices', icon: <Smartphone size={16} />, count: getTypeCount('Mobile Devices') },
        { id: 'Lifting/Slings', label: 'Lifting/Slings', icon: <Anchor size={16} />, count: getTypeCount('Lifting/Slings') },
        { id: 'Furniture', label: 'Furniture', icon: <Armchair size={16} />, count: getTypeCount('Furniture') },
    ];

    const getStatusStyles = (status: AssetStatus) => {
        switch (status) {
            case 'Assigned':
                return 'bg-blue-50 text-blue-600 border-blue-100';
            case 'Available':
                return 'bg-green-50 text-green-600 border-green-100';
            case 'In Maintenance':
                return 'bg-amber-50 text-amber-600 border-amber-100';
            case 'Needs Repair':
            case 'Damaged':
                return 'bg-red-50 text-red-600 border-red-100';
            default:
                return 'bg-gray-50 text-gray-600 border-gray-100';
        }
    };

    const getTypeIcon = (type: string) => {
        switch (type) {
            case 'Vehicle': return <Truck size={16} />;
            case 'Plant & Equipment': return <Wrench size={16} />;
            case 'Tools': return <Hammer size={16} />;
            case 'IT Equipment': return <Monitor size={16} />;
            case 'Mobile Devices': return <Smartphone size={16} />;
            case 'Lifting/Slings': return <Anchor size={16} />;
            case 'Furniture': return <Armchair size={16} />;
            default: return <Package size={16} />;
        }
    };

    return (
        <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10 px-0 sm:px-4 lg:px-0">
            {/* Main Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Asset Management</h1>
                    <p className="text-gray-500 text-sm font-medium">
                        Manage and track all company assets across departments
                    </p>
                </div>
                {/* Desktop Actions */}
                <div className="hidden md:flex items-center gap-3">
                    {ability.can(ACTIONS.READ, RESOURCES.ASSET_SETTINGS) && (
                        <Button variant="outline" className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-2" onClick={() => navigate('/assets/settings')}>
                            <Settings size={18} />
                            <span>Settings</span>
                        </Button>
                    )}
                    {ability.can(ACTIONS.UPDATE, RESOURCES.ASSET_ASSIGNMENT) && (
                        <Button
                            className="bg-slate-950 hover:bg-slate-900 text-white flex items-center gap-2 px-4 whitespace-nowrap"
                            onClick={() => navigate('/assets/assign')}
                        >
                            <User size={18} />
                            <span>Assign Assets</span>
                        </Button>
                    )}
                    {ability.can(ACTIONS.READ, RESOURCES.ASSET) && (
                        <Button
                            variant="outline"
                            className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-2 px-4 whitespace-nowrap"
                            onClick={() => navigate('/assets/print', { state: { assets } })}
                        >
                            <Printer size={18} />
                            <span>Print Labels</span>
                        </Button>
                    )}
                    {ability.can(ACTIONS.CREATE, RESOURCES.ASSET) && (
                        <Button
                            className="bg-slate-950 hover:bg-slate-900 text-white flex items-center gap-2 px-4 whitespace-nowrap"
                            onClick={() => navigate('/assets/new')}
                        >
                            <Plus size={18} />
                            <span>Add Asset</span>
                        </Button>
                    )}
                </div>

                {/* Mobile Actions Dropdown */}
                <div className="md:hidden">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" className="flex items-center gap-2 w-full justify-center">
                                <MoreVertical size={18} />
                                <span>Actions</span>
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                            {ability.can(ACTIONS.READ, RESOURCES.ASSET_SETTINGS) && (
                                <DropdownMenuItem onClick={() => navigate('/assets/settings')} className="py-3">
                                    <Settings size={18} className="mr-2" />
                                    <span>Settings</span>
                                </DropdownMenuItem>
                            )}
                            {ability.can(ACTIONS.UPDATE, RESOURCES.ASSET_ASSIGNMENT) && (
                                <DropdownMenuItem onClick={() => navigate('/assets/assign')} className="py-3">
                                    <User size={18} className="mr-2" />
                                    <span>Assign Assets</span>
                                </DropdownMenuItem>
                            )}
                            {ability.can(ACTIONS.READ, RESOURCES.ASSET) && (
                                <DropdownMenuItem onClick={() => navigate('/assets/print', { state: { assets } })} className="py-3">
                                    <Printer size={18} className="mr-2" />
                                    <span>Print Labels</span>
                                </DropdownMenuItem>
                            )}
                            {ability.can(ACTIONS.CREATE, RESOURCES.ASSET) && (
                                <DropdownMenuItem
                                    onClick={() => navigate('/assets/new')}
                                    className="py-3 bg-slate-900 text-white m-1 hover:bg-slate-800 focus:bg-slate-800 focus:text-white"
                                >
                                    <Plus size={18} className="mr-2" />
                                    <span>Add Asset</span>
                                </DropdownMenuItem>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            {/* Stats Cards */}
            {ability.can(ACTIONS.READ, RESOURCES.ASSET_DASHBOARD_WIDGET) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all group overflow-hidden relative">
                        <div className="flex justify-between items-start">
                            <div className="z-10">
                                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Total Assets</p>
                                <h3 className="text-2xl font-black text-gray-900 group-hover:scale-110 transition-transform origin-left">{stats.total}</h3>
                            </div>
                            <div className="p-3 bg-gray-50 rounded-2xl group-hover:bg-blue-50 transition-colors z-10">
                                <Package className="text-gray-400 group-hover:text-blue-500 transition-colors" size={24} />
                            </div>
                        </div>
                    </div>

                    {ability.can(ACTIONS.READ, RESOURCES.ASSET_VALUE) && (
                        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all group overflow-hidden relative">
                            <div className="flex justify-between items-start">
                                <div className="z-10">
                                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Total Value</p>
                                    <h3 className="text-2xl font-black text-gray-900 font-mono group-hover:scale-110 transition-transform origin-left">
                                        ${stats.value.toLocaleString()}
                                    </h3>
                                </div>
                                <div className="p-3 bg-gray-50 rounded-2xl group-hover:bg-green-50 transition-colors z-10">
                                    <DollarSign className="text-gray-400 group-hover:text-green-500 transition-colors" size={24} />
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all group overflow-hidden relative">
                        <div className="flex justify-between items-start">
                            <div className="z-10">
                                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Assigned Assets</p>
                                <h3 className="text-2xl font-black text-gray-900 group-hover:scale-110 transition-transform origin-left">{stats.active}</h3>
                                <div className="flex items-center gap-1.5 mt-2 text-green-600 text-[10px] font-bold uppercase tracking-wider">
                                    <TrendingUp size={12} />
                                    <span>Currently in use</span>
                                </div>
                            </div>
                            <div className="p-3 bg-green-50 rounded-2xl group-hover:bg-green-100 transition-colors z-10">
                                <TrendingUp className="text-green-600" size={24} />
                            </div>
                        </div>
                    </div>

                    <div
                        className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:ring-2 hover:ring-orange-100 transition-all group overflow-hidden relative cursor-pointer"
                        onClick={() => navigate('/assets/action-items')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && navigate('/assets/action-items')}
                    >
                        <div className="flex justify-between items-start">
                            <div className="z-10">
                                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Need Attention</p>
                                <h3 className="text-2xl font-black text-gray-900 group-hover:scale-110 transition-transform origin-left">{actionItemsCount}</h3>
                                <div className="flex items-center gap-1.5 mt-2 text-orange-600 text-[10px] font-bold uppercase tracking-wider">
                                    <AlertCircle size={12} />
                                    <span>Require action</span>
                                </div>
                            </div>
                            <div className="flex flex-col items-end gap-2 z-10">
                                <div className="p-3 bg-orange-50 rounded-2xl group-hover:bg-orange-100 transition-colors">
                                    <AlertCircle className="text-orange-600" size={24} />
                                </div>
                                <ChevronRight size={14} className="text-orange-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Content Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="bg-gray-100 p-1 rounded-full w-fit hidden md:flex h-auto mb-6">
                    <TabsTrigger value="assets" className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all">Assets</TabsTrigger>
                    {ability.can(ACTIONS.READ, RESOURCES.ASSET_ELECTRICAL_REGISTER_TAB) && (
                        <TabsTrigger value="electrical" className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all">Electrical Equipment Register</TabsTrigger>
                    )}
                    {ability.can(ACTIONS.READ, RESOURCES.ASSET_LIFTING_REGISTER_TAB) && (
                        <TabsTrigger value="lifting" className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all">Lifting Equipment Register</TabsTrigger>
                    )}
                    {ability.can(ACTIONS.READ, RESOURCES.ASSET_SHCEDULED_MAINTENANCE_TAB) && (
                        <TabsTrigger value="maintenance" className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all">Scheduled Maintenance</TabsTrigger>
                    )}
                    {ability.can(ACTIONS.READ, RESOURCES.ASSET_REPORT_TAB) && (
                        <TabsTrigger value="reports" className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all">Reports</TabsTrigger>
                    )}
                </TabsList>

                {/* Mobile Tab Dropdown */}
                <div className="md:hidden mb-6 flex items-center">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" className="w-full justify-between bg-white border-gray-200 h-12 rounded-xl text-gray-700 font-bold shadow-sm">
                                <span className="truncate mr-4">
                                    {activeTab === 'assets' && 'Assets'}
                                    {activeTab === 'electrical' && 'Electrical Equipment Register'}
                                    {activeTab === 'lifting' && 'Lifting Equipment Register'}
                                    {activeTab === 'maintenance' && 'Scheduled Maintenance'}
                                    {activeTab === 'reports' && 'Reports'}
                                    {activeTab === 'settings' && 'Settings'}
                                </span>
                                <ChevronDown size={18} className="text-gray-400 shrink-0" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="center" className="w-[calc(100vw-2rem)] p-2 rounded-xl border border-gray-100 shadow-md">
                            {[
                                { id: 'assets', label: 'Assets' },
                                ...(ability.can(ACTIONS.READ, RESOURCES.ASSET_ELECTRICAL_REGISTER_TAB) ? [{ id: 'electrical', label: 'Electrical Equipment Register' }] : []),
                                ...(ability.can(ACTIONS.READ, RESOURCES.ASSET_LIFTING_REGISTER_TAB) ? [{ id: 'lifting', label: 'Lifting Equipment Register' }] : []),
                                ...(ability.can(ACTIONS.READ, RESOURCES.ASSET_SHCEDULED_MAINTENANCE_TAB) ? [{ id: 'maintenance', label: 'Scheduled Maintenance' }] : []),
                                ...(ability.can(ACTIONS.READ, RESOURCES.ASSET_REPORT_TAB) ? [{ id: 'reports', label: 'Reports' }] : [])
                            ].map((tab) => (
                                <DropdownMenuItem
                                    key={tab.id}
                                    className={cn(
                                        "rounded-lg text-sm font-bold cursor-pointer py-3 transition-colors",
                                        activeTab === tab.id ? "bg-slate-50 text-slate-900" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                                    )}
                                    onClick={() => setActiveTab(tab.id)}
                                >
                                    <div className="w-full flex items-center justify-between">
                                        <span>{tab.label}</span>
                                        {activeTab === tab.id && <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>}
                                    </div>
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                <TabsContent value="assets" className="mt-0">
                    <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6">
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                            <div>
                                <h4 className="font-bold text-gray-900 text-lg">Assets</h4>
                                <p className="text-gray-500 text-sm font-medium">View and manage all company assets</p>
                            </div>

                            <div className="flex flex-col sm:flex-row items-center gap-4 flex-1 max-w-2xl justify-end">
                                <div className="relative w-full sm:max-w-sm">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                    <Input
                                        placeholder="Search assets..."
                                        className="pl-10 bg-gray-50/50 border-gray-100 focus:bg-white transition-all rounded-xl h-11 text-sm font-medium"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </div>
                                <div className="flex items-center gap-4 w-full sm:w-auto">
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button
                                                variant="outline"
                                                className="w-full sm:w-auto bg-gray-50/50 border-gray-100 hover:bg-white transition-all rounded-xl h-11 text-sm font-bold flex items-center justify-between gap-2 px-4"
                                            >
                                                <div className="flex items-center gap-2">
                                                    <ArrowUpDown size={16} className="text-gray-400 shrink-0" />
                                                    <span className="text-gray-700 whitespace-nowrap">
                                                        {sortBy === 'newest' && 'Newest First'}
                                                        {sortBy === 'oldest' && 'Oldest First'}
                                                        {sortBy === 'name_asc' && 'Name (A-Z)'}
                                                        {sortBy === 'name_desc' && 'Name (Z-A)'}
                                                        {sortBy === 'value_high' && 'Highest Value'}
                                                        {sortBy === 'value_low' && 'Lowest Value'}
                                                    </span>
                                                </div>
                                                <ChevronDown size={14} className="text-gray-400 shrink-0" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-[200px] p-2 rounded-xl">
                                            {[
                                                { id: 'newest', label: 'Newest First' },
                                                { id: 'oldest', label: 'Oldest First' },
                                                { id: 'name_asc', label: 'Name (A-Z)' },
                                                { id: 'name_desc', label: 'Name (Z-A)' },
                                                { id: 'value_high', label: 'Highest Value' },
                                                { id: 'value_low', label: 'Lowest Value' }
                                            ].map((option) => (
                                                <DropdownMenuItem
                                                    key={option.id}
                                                    className={cn(
                                                        "rounded-lg text-sm font-bold cursor-pointer py-2.5",
                                                        sortBy === option.id ? "bg-blue-50 text-blue-600" : "text-gray-600"
                                                    )}
                                                    onClick={() => setSortBy(option.id)}
                                                >
                                                    {option.label}
                                                </DropdownMenuItem>
                                            ))}
                                        </DropdownMenuContent>
                                    </DropdownMenu>

                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button
                                                variant="outline"
                                                className="w-full sm:w-[200px] bg-gray-50/50 border-gray-100 hover:bg-white transition-all rounded-xl h-11 text-sm font-bold flex items-center justify-between gap-2"
                                            >
                                                <div className="flex items-center gap-2 truncate">
                                                    <Filter size={16} className="text-gray-400 shrink-0" />
                                                    <span className={cn("truncate", statusFilter !== 'All Status' ? "text-blue-600" : "text-gray-700")}>
                                                        {statusFilter}
                                                    </span>
                                                </div>
                                                <ChevronDown size={14} className="text-gray-400 shrink-0" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-[200px] p-2 rounded-xl">
                                            <DropdownMenuItem
                                                className={cn("rounded-lg text-sm font-bold cursor-pointer", statusFilter === 'All Status' ? "bg-blue-50 text-blue-600" : "text-gray-600")}
                                                onClick={() => setStatusFilter('All Status')}
                                            >
                                                <div className="flex items-center justify-between w-full">
                                                    <span>All Statuses</span>
                                                    <span className={cn("text-xs px-1.5 py-0.5 rounded-md", statusFilter === 'All Status' ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-500")}>
                                                        {getStatusCount('All Status')}
                                                    </span>
                                                </div>
                                            </DropdownMenuItem>
                                            {['Available', 'Assigned', 'Unassigned', 'In Maintenance', 'Needs Repair', 'Damaged', 'Lost', 'Retired', 'Disposed'].map((status) => {
                                                const count = getStatusCount(status);
                                                return (
                                                    <DropdownMenuItem
                                                        key={status}
                                                        className={cn("rounded-lg text-sm font-bold cursor-pointer", statusFilter === status ? "bg-blue-50 text-blue-600" : "text-gray-600")}
                                                        onClick={() => setStatusFilter(status)}
                                                    >
                                                        <div className="flex items-center justify-between w-full">
                                                            <span>{status}</span>
                                                            <span className={cn("text-[11px] px-1.5 py-0.5 rounded-md", statusFilter === status ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-400 font-medium")}>
                                                                {count}
                                                            </span>
                                                        </div>
                                                    </DropdownMenuItem>
                                                );
                                            })}
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                            </div>
                        </div>

                        {showRecycleBin ? (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-red-50 rounded-xl">
                                            <Trash2 size={18} className="text-red-500" />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-gray-900 text-lg">Recycle Bin</h4>
                                            <p className="text-gray-500 text-sm font-medium">Deleted assets are kept for 180 days before permanent removal</p>
                                        </div>
                                    </div>
                                    <Button
                                        variant="outline"
                                        className="rounded-xl h-9 font-bold text-xs gap-2 flex items-center border-gray-200 px-3 md:px-4"
                                        onClick={() => setShowRecycleBin(false)}
                                    >
                                        <ArrowLeft size={16} />
                                        <span className="hidden md:inline">Back to Assets</span>
                                    </Button>
                                </div>

                                {recycleLoading ? (
                                    <div className="space-y-3">
                                        {[...Array(3)].map((_, i) => (
                                            <div key={i} className="animate-pulse h-20 bg-gray-50 rounded-xl w-full" />
                                        ))}
                                    </div>
                                ) : recycledAssets.length === 0 ? (
                                    <div className="py-20 text-center text-gray-400">
                                        <div className="flex flex-col items-center">
                                            <Trash2 size={48} className="mb-4 opacity-20" />
                                            <p className="font-bold text-gray-500">Recycle bin is empty</p>
                                            <p className="text-xs uppercase tracking-widest mt-1">No deleted assets</p>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {/* Mobile Card View */}
                                        <div className="md:hidden space-y-3">
                                            {recycledAssets.map((asset) => (
                                                <div key={asset.id} className="bg-gray-50/50 border border-gray-100 rounded-2xl p-4 space-y-3">
                                                    <div className="flex items-start justify-between gap-3">
                                                        <div className="flex items-center gap-3 min-w-0">
                                                            <div className={cn(
                                                                "w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border",
                                                                getStatusStyles(asset.status)
                                                            )}>
                                                                {getTypeIcon(asset.type)}
                                                            </div>
                                                            <div className="min-w-0">
                                                                <p className="font-bold text-gray-900 text-sm truncate">{asset.name}</p>
                                                                <p className="text-[10px] font-black text-gray-400 tracking-wider uppercase">{asset.tag}</p>
                                                            </div>
                                                        </div>
                                                        <Badge variant="outline" className={cn("rounded-lg border-0 font-bold px-2.5 py-1 text-[10px] shrink-0", getStatusStyles(asset.status))}>
                                                            {asset.status}
                                                        </Badge>
                                                    </div>

                                                    <div className="grid grid-cols-2 gap-3 text-xs">
                                                        <div className="bg-white rounded-xl px-3 py-2.5 border border-gray-100">
                                                            <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] block mb-0.5">Days Left</span>
                                                            <div className="flex items-center gap-1.5">
                                                                <Clock size={12} className={cn(
                                                                    (asset as any).daysRemaining <= 30 ? 'text-red-500' : (asset as any).daysRemaining <= 90 ? 'text-amber-500' : 'text-gray-400'
                                                                )} />
                                                                <span className={cn(
                                                                    "font-bold",
                                                                    (asset as any).daysRemaining <= 30 ? 'text-red-600' : (asset as any).daysRemaining <= 90 ? 'text-amber-600' : 'text-gray-700'
                                                                )}>
                                                                    {(asset as any).daysRemaining} days
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="bg-white rounded-xl px-3 py-2.5 border border-gray-100">
                                                            <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] block mb-0.5">Deleted</span>
                                                            <span className="font-bold text-gray-700">
                                                                    {toSydneyDate(asset.deletedAt) || '-'}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div className="flex flex-col pt-1 border-t border-gray-100">
                                                        <div className="flex items-center justify-between">
                                                            <div className="text-[10px] text-gray-400 font-medium leading-relaxed">
                                                                {asset.deletedBy?.name && <span>By <span className="font-bold text-gray-500">{asset.deletedBy.name}</span></span>}
                                                                {asset.deletionApprovedBy?.name && (
                                                                    <>
                                                                        <br />
                                                                        <span>Approved by <span className="font-bold text-gray-500">{asset.deletionApprovedBy.name}</span></span>
                                                                    </>
                                                                )}
                                                            </div>
                                                            <Button
                                                                variant="outline"
                                                                className="rounded-xl h-8 w-8 p-0 border-blue-100 text-blue-600 hover:bg-blue-50 font-bold text-xs flex items-center justify-center"
                                                                onClick={() => handleRestore(asset)}
                                                                disabled={restoringId === asset.id}
                                                            >
                                                                <RotateCcw size={14} className={restoringId === asset.id ? 'animate-spin' : ''} />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Desktop Table View */}
                                        <div className="hidden md:block overflow-x-auto no-scrollbar">
                                            <table className="w-full border-collapse">
                                                <thead>
                                                    <tr className="bg-gray-50/50">
                                                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest first:rounded-tl-2xl">Asset</th>
                                                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Status at Deletion</th>
                                                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest hidden lg:table-cell">Deleted Date</th>
                                                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest hidden lg:table-cell">Deleted By</th>
                                                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest hidden xl:table-cell">Approved By</th>
                                                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Days Remaining</th>
                                                        <th className="px-6 py-4 text-right text-xs font-bold text-gray-400 uppercase tracking-widest last:rounded-tr-2xl">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100">
                                                    {recycledAssets.map((asset) => (
                                                        <tr key={asset.id} className="hover:bg-gray-50/50 transition-colors">
                                                            <td className="px-6 py-5">
                                                                <div className="flex items-center gap-3">
                                                                    <div className={cn(
                                                                        "w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border",
                                                                        getStatusStyles(asset.status)
                                                                    )}>
                                                                        {getTypeIcon(asset.type)}
                                                                    </div>
                                                                    <div>
                                                                        <p className="font-bold text-gray-900 text-sm">{asset.name}</p>
                                                                        <p className="text-[10px] font-black text-gray-400 tracking-wider uppercase">{asset.tag}</p>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-5">
                                                                <Badge variant="outline" className={cn("rounded-lg border-0 font-bold px-3 py-1 text-xs", getStatusStyles(asset.status))}>
                                                                    {asset.status}
                                                                </Badge>
                                                            </td>
                                                            <td className="px-6 py-5 hidden lg:table-cell">
                                                                <span className="text-sm font-bold text-gray-600">
                                                                    {toSydneyDate(asset.deletedAt) || '-'}
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-5 hidden lg:table-cell">
                                                                <span className="text-sm font-bold text-gray-600">{asset.deletedBy?.name || '-'}</span>
                                                            </td>
                                                            <td className="px-6 py-5 hidden xl:table-cell">
                                                                <span className="text-sm font-bold text-gray-600">{asset.deletionApprovedBy?.name || '-'}</span>
                                                            </td>
                                                            <td className="px-6 py-5">
                                                                <div className="flex items-center gap-2">
                                                                    <Clock size={14} className={cn(
                                                                        (asset as any).daysRemaining <= 30 ? 'text-red-500' : (asset as any).daysRemaining <= 90 ? 'text-amber-500' : 'text-gray-400'
                                                                    )} />
                                                                    <span className={cn(
                                                                        "text-sm font-bold",
                                                                        (asset as any).daysRemaining <= 30 ? 'text-red-600' : (asset as any).daysRemaining <= 90 ? 'text-amber-600' : 'text-gray-600'
                                                                    )}>
                                                                        {(asset as any).daysRemaining} days
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-5 text-right">
                                                                <Button
                                                                    variant="outline"
                                                                    className="rounded-xl h-9 border-blue-100 text-blue-600 hover:bg-blue-50 font-bold text-xs gap-2 flex items-center"
                                                                    onClick={() => handleRestore(asset)}
                                                                    disabled={restoringId === asset.id}
                                                                >
                                                                    <RotateCcw size={14} className={restoringId === asset.id ? 'animate-spin' : ''} />
                                                                    {restoringId === asset.id ? 'Restoring...' : 'Restore'}
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                )}
                            </div>
                        ) : (
                            <>
                                {/* Type Tabs - Pill Style matching Uniform & PPE */}
                                <div className="w-full">
                                    {/* Desktop View */}
                                    <div className="hidden md:flex items-center gap-2 overflow-x-auto no-scrollbar p-1 bg-gray-50 rounded-2xl w-fit">
                                        {assetTypes.map((type) => (
                                            <button
                                                key={type.id}
                                                onClick={() => { setSelectedType(type.id); setShowRecycleBin(false); }}
                                                className={cn(
                                                    "flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap",
                                                    selectedType === type.id && !showRecycleBin
                                                        ? "bg-white text-blue-600 shadow-sm"
                                                        : "text-gray-400 hover:text-gray-600"
                                                )}
                                            >
                                                {type.icon}
                                                {type.label}
                                                {type.count > 0 && (
                                                    <span className={cn(
                                                        "ml-1 text-[10px] px-1.5 py-0.5 rounded-md",
                                                        selectedType === type.id && !showRecycleBin ? "bg-blue-50 text-blue-600" : "bg-gray-100 text-gray-400"
                                                    )}>
                                                        ({type.count})
                                                    </span>
                                                )}
                                            </button>
                                        ))}
                                        {ability.can(ACTIONS.DELETE, RESOURCES.ASSET) && (
                                            <button
                                                onClick={() => setShowRecycleBin(!showRecycleBin)}
                                                className={cn(
                                                    "flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap",
                                                    showRecycleBin
                                                        ? "bg-white text-red-600 shadow-sm"
                                                        : "text-gray-400 hover:text-gray-600"
                                                )}
                                            >
                                                <Trash2 size={14} />
                                                Recycle Bin
                                                {recycledAssets.length > 0 && (
                                                    <span className={cn(
                                                        "ml-1 text-[10px] px-1.5 py-0.5 rounded-md",
                                                        showRecycleBin ? "bg-red-50 text-red-600" : "bg-red-100 text-red-600"
                                                    )}>
                                                        {recycledAssets.length}
                                                    </span>
                                                )}
                                            </button>
                                        )}
                                    </div>

                                    {/* Mobile View */}
                                    <div className="flex md:hidden items-center mb-4">
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="outline" className="w-full sm:w-auto bg-gray-100/50 border-gray-100 hover:bg-white transition-all rounded-2xl h-11 px-4 text-xs font-bold flex justify-between sm:justify-start items-center gap-2">
                                                    <div className="flex items-center gap-2">
                                                        {showRecycleBin ? (
                                                            <>
                                                                <Trash2 size={16} className="text-red-500" />
                                                                <span className="text-red-600 font-bold">Recycle Bin</span>
                                                                {recycledAssets.length > 0 && (
                                                                    <Badge className="bg-red-100 text-red-600 border-0 text-[10px] font-bold py-0 h-4 min-w-[1.25rem] flex items-center justify-center">
                                                                        {recycledAssets.length}
                                                                    </Badge>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <>
                                                                <LayoutGrid size={16} className="text-gray-400" />
                                                                <span className="text-blue-600 font-bold">{assetTypes.find(t => t.id === selectedType)?.label}</span>
                                                                {assetTypes.find(t => t.id === selectedType)?.count !== undefined && assetTypes.find(t => t.id === selectedType)!.count > 0 && (
                                                                    <Badge className="bg-blue-100 text-blue-600 border-0 text-[10px] font-bold py-0 h-4 min-w-[1.25rem] flex items-center justify-center">
                                                                        {assetTypes.find(t => t.id === selectedType)?.count}
                                                                    </Badge>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                    <ChevronDown size={14} className="text-gray-400 ml-2" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="start" className="w-[calc(100vw-3rem)] sm:w-[240px] p-2 rounded-2xl">
                                                <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-gray-400 font-bold px-3 py-2">Select Asset Type</DropdownMenuLabel>
                                                <DropdownMenuSeparator className="bg-gray-50 mx-2" />
                                                {assetTypes.map((type) => (
                                                    <DropdownMenuItem
                                                        key={type.id}
                                                        className={cn(
                                                            "rounded-xl px-3 py-2.5 text-xs font-bold cursor-pointer mb-1 last:mb-0 transition-all",
                                                            selectedType === type.id ? "bg-blue-50 text-blue-600" : "text-gray-600 hover:bg-gray-50"
                                                        )}
                                                        onClick={() => { setSelectedType(type.id); setShowRecycleBin(false); }}
                                                    >
                                                        <div className="flex items-center justify-between w-full">
                                                            <div className="flex items-center gap-2">
                                                                {type.icon}
                                                                <span>{type.label}</span>
                                                            </div>
                                                            {type.count > 0 && (
                                                                <Badge className={cn(
                                                                    "text-[10px] font-bold py-0 h-4 border-0 min-w-[1.25rem] flex items-center justify-center",
                                                                    selectedType === type.id ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-400"
                                                                )}>
                                                                    {type.count}
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    </DropdownMenuItem>
                                                ))}
                                                {ability.can(ACTIONS.DELETE, RESOURCES.ASSET) && (
                                                    <>
                                                        <DropdownMenuSeparator className="bg-gray-50 mx-2" />
                                                        <DropdownMenuItem
                                                            className={cn(
                                                                "rounded-xl px-3 py-2.5 text-xs font-bold cursor-pointer transition-all",
                                                                showRecycleBin ? "bg-red-50 text-red-600" : "text-gray-600 hover:bg-gray-50"
                                                            )}
                                                            onClick={() => setShowRecycleBin(!showRecycleBin)}
                                                        >
                                                            <div className="flex items-center justify-between w-full">
                                                                <div className="flex items-center gap-2">
                                                                    <Trash2 size={14} />
                                                                    <span>Recycle Bin</span>
                                                                </div>
                                                                {recycledAssets.length > 0 && (
                                                                    <Badge className="bg-red-100 text-red-600 border-0 text-[10px] font-bold py-0 h-4 min-w-[1.25rem] flex items-center justify-center">
                                                                        {recycledAssets.length}
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        </DropdownMenuItem>
                                                    </>
                                                )}
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </div>
                                </div>

                                <div className="overflow-x-auto no-scrollbar md:-mx-6 mt-4 md:mt-0">
                                    <table className="w-full border-collapse block md:table">
                                        <thead className="hidden md:table-header-group">
                                            <tr className="bg-gray-50/50">
                                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest first:rounded-tl-2xl">Asset</th>
                                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest hidden md:table-cell">Type</th>
                                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Status</th>
                                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest hidden lg:table-cell">Assignment</th>
                                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest hidden lg:table-cell">Department</th>
                                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest hidden xl:table-cell">Location</th>
                                                {ability.can(ACTIONS.READ, RESOURCES.ASSET_VALUE) && (
                                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest hidden xl:table-cell">Value</th>
                                                )}
                                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest hidden xl:table-cell">Last Service</th>
                                                <th className="px-6 py-4 text-right text-xs font-bold text-gray-400 uppercase tracking-widest last:rounded-tr-2xl">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="block md:table-row-group space-y-4 md:space-y-0 md:divide-y divide-gray-100">
                                            {loading ? (
                                                [...Array(3)].map((_, i) => (
                                                    <tr key={i} className="animate-pulse block md:table-row bg-white md:bg-transparent border border-gray-100 md:border-0 rounded-2xl md:rounded-none mb-4 md:mb-0">
                                                        <td colSpan={9} className="px-6 py-8 block md:table-cell"><div className="h-10 bg-gray-50 rounded-xl w-full" /></td>
                                                    </tr>
                                                ))
                                            ) : paginatedAssets.length === 0 ? (
                                                <tr className="block md:table-row">
                                                    <td colSpan={9} className="px-6 py-20 text-center text-gray-400 block md:table-cell">
                                                        <div className="flex flex-col items-center">
                                                            <Box size={48} className="mb-4 opacity-20" />
                                                            <p className="font-bold text-gray-500">No assets found</p>
                                                            <p className="text-xs uppercase tracking-widest mt-1">Try adjusting your filters or search query</p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                paginatedAssets.map((asset) => (
                                                    <tr key={asset.id} className="block md:table-row hover:bg-gray-50/50 transition-colors group bg-white md:bg-transparent border border-gray-100 md:border-0 rounded-2xl md:rounded-none mb-4 md:mb-0 relative py-4 md:py-0 shadow-sm md:shadow-none">
                                                        <td className="px-4 md:px-6 py-2 md:py-5 block md:table-cell border-b border-gray-100 md:border-0 pb-4 md:pb-5">
                                                            <div className="flex items-start md:items-center gap-4">
                                                                {asset.image ? (
                                                                    <div className="w-16 h-16 md:w-10 md:h-10 rounded-xl shrink-0 overflow-hidden border border-gray-100 shadow-sm mt-1 md:mt-0">
                                                                        <img
                                                                            src={asset.image}
                                                                            alt={asset.name}
                                                                            className="w-full h-full object-cover"
                                                                            onError={(e) => {
                                                                                // Hide broken image and show fallback
                                                                                const target = e.currentTarget;
                                                                                target.style.display = 'none';
                                                                                const fallback = target.nextElementSibling as HTMLElement;
                                                                                if (fallback) fallback.style.display = 'flex';
                                                                            }}
                                                                        />
                                                                        <div
                                                                            className={cn(
                                                                                "w-full h-full rounded-xl items-center justify-center border",
                                                                                getStatusStyles(asset.status)
                                                                            )}
                                                                            style={{ display: 'none', marginTop: '-100%' }}
                                                                        >
                                                                            {getTypeIcon(asset.type)}
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <div className={cn(
                                                                        "w-16 h-16 md:w-10 md:h-10 rounded-xl flex items-center justify-center shrink-0 border mt-1 md:mt-0",
                                                                        getStatusStyles(asset.status)
                                                                    )}>
                                                                        {getTypeIcon(asset.type)}
                                                                    </div>
                                                                )}
                                                                <div className="min-w-0 flex-1">
                                                                    <div className="flex justify-between items-start md:block">
                                                                        <p className="font-bold text-gray-900 truncate text-base md:text-sm">{asset.name}</p>
                                                                        {/* Only show on mobile top corner */}
                                                                        <Badge variant="outline" className={cn("rounded-lg border-0 font-bold px-2.5 py-1 text-[10px] md:hidden shrink-0 ml-2", getStatusStyles(asset.status))}>
                                                                            {asset.status}
                                                                        </Badge>
                                                                    </div>
                                                                    <p className="text-[11px] md:text-[10px] font-black text-gray-400 tracking-wider uppercase mt-1 md:mt-0">
                                                                        {asset.tag} • {asset.brand} {asset.model}
                                                                    </p>

                                                                    {/* Mobile-only assignment pill */}
                                                                    <div className="md:hidden mt-3 flex items-center gap-2">
                                                                        <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-gray-500">
                                                                            {asset.assignedToUser || asset.assignedToDepartment ? <User size={12} /> : <AlertCircle size={12} />}
                                                                        </div>
                                                                        <span className={cn(
                                                                            "text-xs font-bold truncate",
                                                                            asset.assignedToUser || asset.assignedToDepartment ? "text-gray-700" : "text-gray-400 font-medium"
                                                                        )}>
                                                                            {asset.assignedToUser?.name || (asset.assignedToDepartment ? (asset.assignedToDepartment.departmentManager?.name || 'No manager') : 'Unassigned')}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>

                                                        {/* Hidden on mobile, shown as table cell on desktop */}
                                                        <td className="px-6 py-5 hidden md:table-cell">
                                                            <div className="flex items-center gap-2 text-gray-600 font-bold text-sm">
                                                                <span className="opacity-40">{getTypeIcon(asset.type)}</span>
                                                                {asset.type}
                                                            </div>
                                                        </td>

                                                        {/* Hidden on mobile (shown in asset card top corner instead) */}
                                                        <td className="px-6 py-5 hidden md:table-cell">
                                                            <Badge variant="outline" className={cn("rounded-lg border-0 font-bold px-3 py-1 text-xs", getStatusStyles(asset.status))}>
                                                                {asset.status}
                                                            </Badge>
                                                        </td>

                                                        {/* Assignment cell - hidden on mobile (shown below asset name), large screens only */}
                                                        <td className="px-6 py-5 hidden lg:table-cell">
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-gray-500">
                                                                    {asset.assignedToUser || asset.assignedToDepartment ? <User size={14} /> : <AlertCircle size={14} />}
                                                                </div>
                                                                <span className={cn(
                                                                    "text-sm font-bold truncate",
                                                                    asset.assignedToUser || asset.assignedToDepartment ? "text-gray-700" : "text-gray-400 font-medium"
                                                                )}>
                                                                    {asset.assignedToUser?.name || (asset.assignedToDepartment ? (asset.assignedToDepartment.departmentManager?.name || 'No manager') : 'Unassigned')}
                                                                </span>
                                                            </div>
                                                        </td>

                                                        {/* Department cell */}
                                                        <td className="px-6 py-5 hidden lg:table-cell">
                                                            <div className="flex items-center gap-2 text-gray-600 font-bold text-sm">
                                                                <Building2 size={16} className="text-gray-400 opacity-50" />
                                                                {asset.assignedToUser?.department?.departmentName || asset.assignedToDepartment?.departmentName || '-'}
                                                            </div>
                                                        </td>

                                                        {/* Location cell */}
                                                        <td className="px-6 py-5 hidden xl:table-cell">
                                                            <div className="flex items-center gap-2 text-gray-600 font-bold text-sm">
                                                                {asset.location || '-'}
                                                            </div>
                                                        </td>

                                                        {/* Extra data row for Mobile / Desktop XL column */}
                                                        {ability.can(ACTIONS.READ, RESOURCES.ASSET_VALUE) && (
                                                            <td className="px-4 md:px-6 py-3 md:py-5 flex items-center justify-between md:table-cell md:hidden xl:table-cell border-b border-gray-100 md:border-0 bg-gray-50/50 md:bg-transparent rounded-lg mx-4 md:mx-0 mt-3 md:mt-0 font-mono text-sm">
                                                                <span className="text-gray-400 text-xs font-bold uppercase tracking-widest md:hidden font-sans">Value</span>
                                                                <span className="font-bold text-gray-700">
                                                                    {asset.purchasePrice ? `$${Number(asset.purchasePrice).toLocaleString()}` : '-'}
                                                                </span>
                                                            </td>
                                                        )}

                                                        {/* Extra data row for Mobile / Desktop XL column */}
                                                        <td className="px-4 md:px-6 py-3 md:py-5 flex items-center justify-between md:table-cell md:hidden xl:table-cell">
                                                            <span className="text-gray-400 text-xs font-bold uppercase tracking-widest md:hidden">Last Service</span>
                                                            <div className="flex items-center gap-2 text-sm font-bold text-gray-400">
                                                                <Calendar size={14} />
                                                                {asset.lastServiceDate ? toSydneyDate(asset.lastServiceDate) : 'No service'}
                                                            </div>
                                                        </td>

                                                        {/* Actions */}
                                                        <td className="px-4 md:px-6 py-4 md:py-5 block md:table-cell border-t border-gray-50 md:border-t-0 mt-3 md:mt-0">
                                                            <div className="flex items-center justify-end gap-2 w-full">
                                                                <Button
                                                                    variant="outline"
                                                                    className="h-10 w-10 md:h-9 md:w-9 p-0 border-gray-200 hover:bg-blue-50 rounded-xl hover:text-blue-600 hover:border-blue-100 transition-all justify-center"
                                                                    onClick={() => navigate(`/assets/${asset.id}`)}
                                                                >
                                                                    <Eye size={18} className="shrink-0" />
                                                                </Button>
                                                                {ability.can(ACTIONS.UPDATE, RESOURCES.ASSET) && (
                                                                    <Button
                                                                        variant="outline"
                                                                        className="h-10 w-10 md:h-9 md:w-9 p-0 border-gray-200 hover:bg-slate-100 rounded-xl text-gray-600 transition-all justify-center"
                                                                        onClick={() => navigate(`/assets/${asset.id}/edit`)}
                                                                    >
                                                                        <Edit3 size={18} className="shrink-0" />
                                                                    </Button>
                                                                )}
                                                                {ability.can(ACTIONS.DELETE, RESOURCES.ASSET) && (
                                                                    <Button
                                                                        variant="outline"
                                                                        className="h-10 w-10 md:h-9 md:w-9 p-0 border-gray-200 hover:bg-red-50 rounded-xl hover:text-red-600 hover:border-red-100 text-gray-600 transition-all justify-center"
                                                                        onClick={() => handleDeleteClick(asset)}
                                                                    >
                                                                        <Trash2 size={18} className="shrink-0" />
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {totalPages > 1 && (
                                    <div className="mt-6 flex justify-center pb-4">
                                        <Pagination>
                                            <PaginationContent className="flex flex-wrap justify-center gap-1 sm:gap-2">
                                                <PaginationItem>
                                                    <PaginationPrevious
                                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                                        className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                                                    />
                                                </PaginationItem>

                                                {[...Array(totalPages)].map((_, i) => {
                                                    const page = i + 1;

                                                    // Show first, last, current, and adjacent pages
                                                    if (
                                                        page === 1 ||
                                                        page === totalPages ||
                                                        (page >= currentPage - 1 && page <= currentPage + 1)
                                                    ) {
                                                        return (
                                                            <PaginationItem key={page}>
                                                                <PaginationLink
                                                                    onClick={() => setCurrentPage(page)}
                                                                    isActive={currentPage === page}
                                                                    className="cursor-pointer"
                                                                >
                                                                    {page}
                                                                </PaginationLink>
                                                            </PaginationItem>
                                                        );
                                                    }

                                                    // Show ellipsis for gaps
                                                    if (
                                                        page === currentPage - 2 ||
                                                        page === currentPage + 2
                                                    ) {
                                                        return (
                                                            <PaginationItem key={page}>
                                                                <PaginationEllipsis />
                                                            </PaginationItem>
                                                        );
                                                    }

                                                    return null;
                                                })}

                                                <PaginationItem>
                                                    <PaginationNext
                                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                                        className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                                                    />
                                                </PaginationItem>
                                            </PaginationContent>
                                        </Pagination>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </TabsContent>

                <TabsContent value="settings" className="mt-0">
                    <div className="mt-8">
                        <AssetSettingsPage />
                    </div>
                </TabsContent>

                {ability.can(ACTIONS.READ, RESOURCES.ASSET_ELECTRICAL_REGISTER_TAB) && (
                    <TabsContent value="electrical" className="mt-0 space-y-6">
                        <ElectricalRegisterList
                            onCreateNew={() => navigate('/assets/electrical-inspections/new')}
                        />
                    </TabsContent>
                )}

                {ability.can(ACTIONS.READ, RESOURCES.ASSET_LIFTING_REGISTER_TAB) && (
                    <TabsContent value="lifting" className="mt-0 space-y-6">
                        <LiftingRegisterList
                            onCreateNew={() => navigate('/assets/lifting-inspections/new')}
                        />
                    </TabsContent>
                )}

                {ability.can(ACTIONS.READ, RESOURCES.ASSET_REPORT_TAB) && (
                    <TabsContent value="reports" className="mt-0">
                        <AssetReports />
                    </TabsContent>
                )}

                {ability.can(ACTIONS.READ, RESOURCES.ASSET_SHCEDULED_MAINTENANCE_TAB) && (
                    <TabsContent value="maintenance" className="mt-0 pt-6">
                        <MaintenanceCalendar />
                    </TabsContent>
                )}


            </Tabs>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                <AlertDialogContent className="rounded-2xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                            <Trash2 size={20} />
                            Delete Asset
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-gray-600">
                            <span className="font-bold text-gray-900 block mb-2">{assetToDelete?.name} ({assetToDelete?.tag})</span>
                            This asset will be moved to the recycle bin for 180 days. After 180 days, the asset will be <span className="font-bold text-red-600">permanently deleted</span> and cannot be recovered. You can reinstate it from the Recycle Bin tab during this period.
                        </AlertDialogDescription>
                    </AlertDialogHeader>

                    <div className="space-y-3 py-2">
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Current Status:</span>
                            <Badge variant="outline" className={cn("rounded-lg border-0 font-bold px-2.5 py-0.5 text-[10px]", getStatusStyles(assetToDelete?.status || 'Available'))}>
                                {assetToDelete?.status}
                            </Badge>
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block">
                                Approved By <span className="text-red-500">*</span>
                            </label>
                            <Select value={selectedApprover} onValueChange={setSelectedApprover}>
                                <SelectTrigger className="rounded-xl h-11">
                                    <SelectValue placeholder="Select who approved the deletion..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {approvers.map((approver) => (
                                        <SelectItem key={approver.id} value={approver.id.toString()}>
                                            {approver.name} — {approver.userRole}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {!selectedApprover && (
                                <p className="text-xs text-amber-600 font-medium flex items-center gap-1">
                                    <AlertTriangle size={12} />
                                    Please select a Manager, Director, Senior Manager, or Admin who approved this deletion
                                </p>
                            )}
                        </div>
                    </div>

                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl" onClick={() => { setShowDeleteConfirm(false); setAssetToDelete(null); setSelectedApprover(''); }}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className="rounded-xl bg-red-600 hover:bg-red-700 text-white"
                            disabled={!selectedApprover || deleting}
                            onClick={(e) => {
                                e.preventDefault();
                                confirmDelete();
                            }}
                        >
                            {deleting ? 'Deleting...' : 'Delete Asset'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Delete Warning Dialog */}
            <AlertDialog open={showDeleteWarning} onOpenChange={setShowDeleteWarning}>
                <AlertDialogContent className="rounded-2xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
                            <AlertTriangle size={20} />
                            Cannot Delete Asset
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-gray-600">
                            This asset's current status is <Badge variant="outline" className={cn("rounded-lg border-0 font-bold px-2.5 py-0.5 text-[10px] mx-1", getStatusStyles(deleteWarningStatus as AssetStatus))}>{deleteWarningStatus}</Badge>.
                            <span className="block mt-2">An asset can only be deleted when its status is <span className="font-bold">Damaged</span>, <span className="font-bold">Needs Repair</span>, <span className="font-bold">Disposed</span>, or <span className="font-bold">Lost</span>.</span>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogAction className="rounded-xl" onClick={() => setShowDeleteWarning(false)}>OK</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default AssetDashboard;
